// Idempotent file scaffolder. Writes per-type starter files into a service
// folder, never overwriting anything that already exists. Used by all three
// scaffold commands (`scaffold`, `scaffold service`, `scaffold rename` does
// not call this).

import { existsSync, mkdirSync, writeFileSync } from 'node:fs';
import { dirname, join } from 'node:path';
import type { ScaffoldFile, ServiceType } from './templates.js';
import { templateFor } from './templates.js';

export interface ScaffoldResult {
  /** Files actually written this run. */
  written: string[];
  /** Files skipped because they already exist (preserved user edits). */
  skipped: string[];
}

/**
 * Scaffold the per-type starter files into `services/<name>/`. Creates the
 * directory tree if absent. Existing files are left untouched.
 *
 * `cwd` is the project root (where `cloudgrid.yaml` lives).
 */
export function scaffoldService(cwd: string, name: string, type: ServiceType): ScaffoldResult {
  const baseDir = join(cwd, 'services', name);
  mkdirSync(baseDir, { recursive: true });

  const files = templateFor(type, name);
  return writeFiles(baseDir, files);
}

function writeFiles(baseDir: string, files: ScaffoldFile[]): ScaffoldResult {
  const written: string[] = [];
  const skipped: string[] = [];
  for (const f of files) {
    const target = join(baseDir, f.relPath);
    if (existsSync(target)) {
      skipped.push(target);
      continue;
    }
    mkdirSync(dirname(target), { recursive: true });
    writeFileSync(target, f.content);
    written.push(target);
  }
  return { written, skipped };
}
